import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Badge, Card, Flex, Icon, Text, cn } from '@kognitos/lattice';

import {
  CATALOG_BANNER_ACTIVITY_COUNT,
  CATALOG_BANNER_WORKSTREAMS,
} from '@/modules/process-catalog/banner-preview';
import { CatalogFolder } from '@/modules/process-catalog/components/CatalogFolder';
import { catalogWorkstreamAccentVar } from '@/modules/process-catalog/workstream-accent';
import { useScrollEdges } from '@/shared/hooks/use-scroll-edges';

/** Folder pitch down the strip: 132px of folder plus a 12px gap. */
const FOLDER_PITCH = 144;
/**
 * The strip's left inset. It exists so the first folder's left-hand pill fans
 * out fully inside the banner instead of being cut off by its left edge.
 */
const STRIP_INSET = 12;
/** Half a folder — the point the fan radiates from. */
const FOLDER_CENTER = 66;
/** How much of the 60px folder the strip shows. */
const STRIP_VISIBLE_HEIGHT = 52;
/** Slack when deciding whether the strip is at an end. */
const EDGE_TOLERANCE = 1;

/** Resting transform for a fan pill: tucked into the folder, invisible. */
const FAN_REST = 'translate(-50%, 8px) scale(0.9)';

/**
 * The three pills' open transforms and their stagger, left to right.
 * The stagger applies in both directions, so they close in the same order they opened.
 */
const FAN_PILLS = [
  {
    open: 'translate(calc(-50% - 56px), -20px) rotate(-9deg)',
    delayMs: 0,
  },
  { open: 'translate(-50%, -36px)', delayMs: 60 },
  {
    open: 'translate(calc(-50% + 56px), -20px) rotate(9deg)',
    delayMs: 120,
  },
] as const;

export interface ICatalogBannerProps {
  title?: string;
  countLabel?: string;
  workstreams?: readonly {
    workstream: string;
    label: string;
    subLabels: readonly string[];
  }[];
  /** Opens the modal, drilled into the clicked L1 or at rest when undefined. */
  onOpen: (workstream?: string) => void;
  /** Warms the modal's lazy chunk on pointer enter or focus, once per mount. */
  onPrefetch?: () => void;
  /** The banner surface, so the caller can hand focus back when the modal closes. */
  ref?: Ref<HTMLDivElement>;
  className?: string;
}

/**
 * The process catalog's folders, as the banner shows them by default: one per
 * workstream, sorted by its visible label so the order follows the locale.
 *
 * @param t - A `drafts` namespace translator
 * @returns The banner's `workstreams`
 */
export function getDefaultCatalogBannerFolders(t: TFunction<'drafts'>) {
  return CATALOG_BANNER_WORKSTREAMS.map(ws => ({
    workstream: ws.workstream,
    label: t(ws.labelKey),
    subLabels: ws.subKeys.map(key => t(key)),
  })).sort((a, b) => a.label.localeCompare(b.label));
}

/** One process-group pill in a folder's fan. */
function WorkstreamPill({
  label,
  accentVar,
}: {
  label: string;
  accentVar: string;
}) {
  return (
    <Badge
      className="rounded-[8px] border-0 px-[8px] py-[2px] text-[9px] leading-[1.4] text-primary-foreground shadow-sm"
      style={{
        backgroundColor: `color-mix(in srgb, var(${accentVar}) 36%, white)`,
      }}
    >
      {label}
    </Badge>
  );
}

/** The surface every catalog banner sits on, so the banner types read the same. */
export const CATALOG_BANNER_SURFACE =
  'w-full overflow-hidden rounded-[14px] border-0 bg-muted/50 px-[18px] pt-4 text-left backdrop-blur-sm hover:shadow-none';

/** The banner header's focus outline, inset to match the surface's corners. */
export const CATALOG_BANNER_HEADER = 'focus-ring rounded-[6px]';

/**
 * The catalog banner: a scrollable strip of tinted folders cropped at the
 * banner's edge. Hovering or focusing one fans three group pills above it; each
 * is a named control that opens the modal on that folder, while the header
 * opens it at rest. Every layer inside stays aria-hidden, since ARIA doesn't
 * make a control's descendants presentational. `title`, `countLabel` and
 * `workstreams` let each catalog supply its own copy and folders; without
 * `workstreams` it shows the process catalog's APQC L1 folders.
 *
 * @param props - The open callback, and optional title, count and folders.
 * @returns The banner.
 */
export function CatalogBanner({
  onOpen,
  onPrefetch,
  ref,
  className,
  title,
  countLabel,
  workstreams,
}: ICatalogBannerProps) {
  const { t } = useTranslation('drafts');
  // Default folders run alphabetically by their visible tab label, sorted
  // here so the order follows the active locale. The fan overlay positions
  // pills by index, so both maps must share this array.
  const folders = useMemo(
    () => workstreams ?? getDefaultCatalogBannerFolders(t),
    [t, workstreams],
  );
  const heading = title ?? t('drafts.processCatalog.banner.title');
  const [hovered, setHovered] = useState<number | null>(null);

  // The fan sits outside the scrolling row so the pills aren't clipped, which
  // means it has to be offset by hand or they detach from their folders.
  const [scrollLeft, setScrollLeft] = useState(0);

  // A fade only means "there is more this way". Both start hidden, and the
  // first measure runs as the strip mounts, before paint. The hook also
  // re-measures on resize: the banner is as wide as the chat column, so a
  // window resize can change whether the row overflows with no scroll event.
  const { edges, track } = useScrollEdges({ tolerance: EDGE_TOLERANCE });
  const stripRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => track(stripRef.current), [track]);

  // Pointer enter fires on every folder crossing and focus can bounce, so the
  // caller's callback is spent once even though the registry would dedupe it.
  const prefetched = useRef(false);
  const prefetch = () => {
    if (prefetched.current) return;
    prefetched.current = true;
    onPrefetch?.();
  };

  // Card is a div surface, so button-like keyboard activation is ours to give.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <Card
      ref={ref}
      role="group"
      tabIndex={-1}
      aria-label={heading}
      onClick={() => onOpen()}
      onPointerEnter={prefetch}
      onFocus={prefetch}
      data-testid="catalog-banner"
      className={cn(
        CATALOG_BANNER_SURFACE,
        'focus-ring cursor-pointer gap-6 pb-0',
        className,
      )}
    >
      <Flex
        align="center"
        justify="between"
        width="full"
        role="button"
        tabIndex={0}
        aria-label={heading}
        onKeyDown={handleKeyDown}
        data-testid="catalog-banner-open"
        className={CATALOG_BANNER_HEADER}
      >
        <Text as="span" level="small" className="font-normal">
          {heading}
        </Text>
        <Flex align="center" gap={2}>
          <Text as="span" level="xSmall" color="muted">
            {countLabel ??
              t('drafts.processCatalog.activityCount', {
                count: CATALOG_BANNER_ACTIVITY_COUNT,
              })}
          </Text>
          <Icon
            type="ChevronRight"
            size="sm"
            className="text-muted-foreground"
          />
        </Flex>
      </Flex>

      <Flex direction="column" align="stretch" position="relative" width="full">
        {/* Horizontally scrollable. */}
        <Flex
          ref={stripRef}
          direction="column"
          align="stretch"
          position="relative"
          className="no-scrollbar overflow-x-auto overflow-y-hidden overscroll-x-contain"
          style={{ height: `${STRIP_VISIBLE_HEIGHT}px` }}
          onScroll={event => setScrollLeft(event.currentTarget.scrollLeft)}
          data-testid="catalog-banner-strip"
        >
          <Flex
            align="stretch"
            gap={3}
            className="w-max"
            style={{ paddingLeft: `${STRIP_INSET}px` }}
          >
            {folders.map((ws, index) => (
              <CatalogFolder
                key={ws.workstream}
                workstream={ws.workstream}
                label={ws.label}
                hovered={hovered === index}
                className="cursor-pointer"
                onSelect={() => onOpen(ws.workstream)}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
              />
            ))}
          </Flex>
        </Flex>

        {/* Both fades are siblings of the scroll container. Out here they are 
        pinned to the banner's own edges, which is where the window onto the row actually ends.*/}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-0 bottom-0 z-10 w-[60px] transition-opacity duration-200"
          style={{
            background: 'linear-gradient(to left, transparent, var(--muted))',
            opacity: edges.left ? 1 : 0,
          }}
          data-testid="catalog-banner-fade-left"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-[60px] transition-opacity duration-200"
          style={{
            background: 'linear-gradient(to right, transparent, var(--muted))',
            opacity: edges.right ? 1 : 0,
          }}
          data-testid="catalog-banner-fade-right"
        />

        {/* The fan lives OUTSIDE the clipped row, as its sibling. The pills rise
            34px above the strip and reach 56px to either side of a folder's centre */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-50"
          style={{ transform: `translateX(${-scrollLeft}px)` }}
          data-testid="catalog-banner-fan"
        >
          {folders.map((ws, index) => {
            const isOpen = hovered === index;
            const accentVar = catalogWorkstreamAccentVar(ws.workstream);
            return (
              <div
                key={ws.workstream}
                className="absolute top-[2px]"
                style={{
                  left: `${STRIP_INSET + index * FOLDER_PITCH + FOLDER_CENTER}px`,
                }}
              >
                {FAN_PILLS.slice(0, ws.subLabels.length).map(
                  (pill, pillIndex) => (
                    <div
                      // Keyed by fan slot: a catalog can repeat a group label.
                      key={pill.open}
                      className="absolute top-0 left-0"
                      style={{
                        transform: isOpen ? pill.open : FAN_REST,
                        opacity: isOpen ? 1 : 0,
                        transition: [
                          `transform 340ms cubic-bezier(.3,.8,.4,1) ${pill.delayMs}ms`,
                          `opacity 260ms ease ${pill.delayMs}ms`,
                        ].join(', '),
                      }}
                    >
                      <WorkstreamPill
                        label={ws.subLabels[pillIndex]}
                        accentVar={accentVar}
                      />
                    </div>
                  ),
                )}
              </div>
            );
          })}
        </div>
      </Flex>
    </Card>
  );
}
